<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { MenuItem } from '@/services/orderService';

interface Props {
  actionType: string;
  formData: {
    action: {
      type: string;
      value: number;
      free_item_id?: number;
      apply_to?: string;
      required_quantity?: number;
      target_item_id?: number;
    };
  };
  menuItems: MenuItem[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:formData': [value: any];
}>();

const promotionType = ref<'same_product' | 'different_product'>('same_product');

const formData = computed({
  get: () => props.formData,
  set: (value) => emit('update:formData', value)
});

// Watch for promotion type changes to configure action values
watch(promotionType, (newType) => {
  if (newType === 'same_product') {
    // Para 2x1 del mismo producto: value = 0, free_item_id = null
    formData.value.action.value = 0;
    formData.value.action.free_item_id = undefined;
  } else {
    // Para producto diferente: mantener valores existentes o establecer defaults
    if (formData.value.action.value === 0) {
      formData.value.action.value = 1; // valor por defecto para buy_x_get_y
    }
  }
});
</script>

<template>
  <div
    v-if="actionType"
    class="border-t border-gray-200 dark:border-gray-700 pt-6"
  >
    <h4 class="text-lg font-medium text-gray-900 dark:text-white mb-4">
      Configuración de la Promoción
    </h4>
    
    <!-- Percentage Discount -->
    <div
      v-if="actionType === 'percentage_discount'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Porcentaje de Descuento
        </label>
        <input
          :value="formData.action.value"
          type="number"
          step="0.1"
          min="0"
          max="100"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @input="formData.action.value = Number(($event.target as HTMLInputElement).value)"
        >
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Aplicar a
        </label>
        <select
          :value="formData.action.apply_to"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @change="formData.action.apply_to = ($event.target as HTMLSelectElement).value"
        >
          <option value="all">
            Todos los items
          </option>
          <option value="cheapest">
            Item más barato
          </option>
          <option value="most_expensive">
            Item más caro
          </option>
        </select>
      </div>
    </div>

    <!-- Fixed Amount Discount -->
    <div
      v-else-if="actionType === 'fixed_amount_discount'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Monto de Descuento
        </label>
        <input
          :value="formData.action.value"
          type="number"
          step="0.01"
          min="0"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @input="formData.action.value = Number(($event.target as HTMLInputElement).value)"
        >
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Aplicar a
        </label>
        <select
          :value="formData.action.apply_to"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @change="formData.action.apply_to = ($event.target as HTMLSelectElement).value"
        >
          <option value="all">
            Todos los items
          </option>
          <option value="cheapest">
            Item más barato
          </option>
          <option value="most_expensive">
            Item más caro
          </option>
        </select>
      </div>
    </div>

    <!-- Fixed Price -->
    <div
      v-else-if="actionType === 'fixed_price'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Precio Fijo
        </label>
        <input
          :value="formData.action.value"
          type="number"
          step="0.01"
          min="0"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @input="formData.action.value = Number(($event.target as HTMLInputElement).value)"
        >
      </div>
    </div>

    <!-- Free Item -->
    <div
      v-else-if="actionType === 'free_item'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Item Gratis
        </label>
        <select
          :value="formData.action.free_item_id"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @change="formData.action.free_item_id = Number(($event.target as HTMLSelectElement).value)"
        >
          <option value="">
            Seleccionar item
          </option>
          <option
            v-for="item in menuItems"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Buy X Get Y -->
    <div
      v-else-if="actionType === 'buy_x_get_y'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo de Promoción 2x1
        </label>
        <select
          v-model="promotionType"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="same_product">
            Mismo producto (compra 2, el 2do gratis)
          </option>
          <option value="different_product">
            Producto diferente (compra X, obtén Y gratis)
          </option>
        </select>
      </div>
      
      <!-- Solo mostrar item gratis si es tipo different_product -->
      <div v-if="promotionType === 'different_product'">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Item a Obtener Gratis
        </label>
        <select
          :value="formData.action.free_item_id"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @change="formData.action.free_item_id = Number(($event.target as HTMLSelectElement).value)"
        >
          <option value="">
            Seleccionar item
          </option>
          <option
            v-for="item in menuItems"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </option>
        </select>
      </div>
      
      <!-- Para 2x1 del mismo producto, el value debe ser 0 y no se usa free_item_id -->
      <div v-else>
        <input
          type="hidden"
          :value="formData.action.value"
        >
        <input
          type="hidden"
          :value="formData.action.free_item_id"
        >
      </div>
    </div>

    <!-- Bundle Price -->
    <div
      v-else-if="actionType === 'bundle_price'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Precio del Paquete
        </label>
        <input
          :value="formData.action.value"
          type="number"
          step="0.01"
          min="0"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @input="formData.action.value = Number(($event.target as HTMLInputElement).value)"
        >
      </div>
    </div>

    <!-- Buy X for Y Price -->
    <div
      v-else-if="actionType === 'buy_x_for_y_price'"
      class="space-y-3"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Item para la Promoción
        </label>
        <select
          :value="formData.action.target_item_id"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @change="formData.action.target_item_id = Number(($event.target as HTMLSelectElement).value)"
        >
          <option value="">
            Seleccionar item
          </option>
          <option
            v-for="item in menuItems"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Cantidad Requerida
          </label>
          <input
            :value="formData.action.required_quantity"
            type="number"
            min="1"
            required
            placeholder="Ej: 2"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            @input="formData.action.required_quantity = Number(($event.target as HTMLInputElement).value)"
          >
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Precio Especial
          </label>
          <input
            :value="formData.action.value"
            type="number"
            step="0.01"
            min="0"
            required
            placeholder="Ej: 250"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            @input="formData.action.value = Number(($event.target as HTMLInputElement).value)"
          >
        </div>
      </div>
    </div>
  </div>
</template>
